/**
 * BootGate
 *
 * SUITE STANDARD v2.3.0:
 * - Root gate wrapping ALL authenticated routes (including /central-diagnostics)
 * - Calls switch-context on mount; renders overlays or children based on result
 * - Fail-closed: Central unreachable → CentralUnavailableOverlay, no bypass
 * - Only /auth/callback is exempt (pre-auth OAuth redirect landing)
 * - Token change detection: re-runs boot on workspace switch callback
 * - StrictMode safe: fetchingRef prevents duplicate concurrent calls
 */

import { useEffect, useRef, ReactNode } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '../store';
import {
  bootStarted,
  bootSuccess,
  bootCentralUnavailable,
  bootWorkspaceClosed,
  bootReset,
} from '../store/slices/bootSlice';
import { clearCredentials } from '../store/slices/authSlice';
import { fetchSwitchContext } from '../lib/centralBootstrap';
import { logout } from '../lib/centralAuth';
import { LoadingSpinner } from './ui/LoadingSpinner';
import { WorkspaceClosedOverlay } from './overlays/WorkspaceClosedOverlay';
import { CentralUnavailableOverlay } from './overlays/CentralUnavailableOverlay';

interface BootGateProps {
  children: ReactNode;
}

export function BootGate({ children }: BootGateProps) {
  const dispatch = useDispatch<AppDispatch>();
  const bootStatus = useSelector((state: RootState) => state.boot.status);
  const bootError = useSelector((state: RootState) => state.boot.error);
  const token = useSelector((state: RootState) => state.auth.token);
  const prevTokenRef = useRef<string | null>(null);
  const fetchingRef = useRef(false);

  const runBoot = async () => {
    // Guard against concurrent calls (React StrictMode double-mount)
    if (fetchingRef.current) return;
    fetchingRef.current = true;

    dispatch(bootStarted());
    const result = await fetchSwitchContext();

    fetchingRef.current = false;

    switch (result.type) {
      case 'success':
        dispatch(
          bootSuccess({
            user: result.data.user,
            currentWorkspace: result.data.currentWorkspace,
            otherWorkspaces: result.data.otherWorkspaces,
            apps: result.data.apps,
            entitlementsEffective: result.data.entitlementsEffective ?? [],
            meta: result.data.meta ?? null,
          })
        );
        break;

      case 'unauthorized':
        logout();
        dispatch(clearCredentials());
        break;

      case 'workspace_closed':
        dispatch(
          bootWorkspaceClosed({
            workspaceName: result.workspaceName,
            otherWorkspaces: result.otherWorkspaces,
          })
        );
        break;

      case 'unavailable':
        dispatch(bootCentralUnavailable(result.message));
        break;
    }
  };

  // Run boot on mount when idle
  useEffect(() => {
    if (bootStatus === 'idle' && token) {
      runBoot();
    }
  }, [bootStatus, token]);

  // Detect token change (workspace switch callback) → reset and re-run
  useEffect(() => {
    if (prevTokenRef.current !== null && token !== prevTokenRef.current && token) {
      dispatch(bootReset());
      // bootReset sets status to 'idle', which triggers the effect above
    }
    prevTokenRef.current = token;
  }, [token, dispatch]);

  // Render based on boot status
  switch (bootStatus) {
    case 'idle':
    case 'loading':
      return (
        <div className="flex h-screen items-center justify-center">
          <div className="text-center">
            <LoadingSpinner size="lg" />
            <p className="mt-4 text-sm text-gray-500">Loading workspace...</p>
          </div>
        </div>
      );

    case 'workspace_closed':
      return <WorkspaceClosedOverlay />;

    case 'central_unavailable':
      return (
        <CentralUnavailableOverlay
          errorMessage={bootError ?? 'Unknown error'}
          onRetry={runBoot}
        />
      );

    case 'ready':
      return <>{children}</>;

    default:
      return null;
  }
}
